/**
 * @fileoverview Attachment thumbnail — resolves photo ID to visual thumbnail.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Uses the host-injected ThumbnailResolver to display visual photo thumbnails
 *   inside CameraField and PhotoField widgets, and the violation capture surface.
 * - Falls back to a placeholder icon when no resolver is injected or resolution fails.
 * - Optionally supports a tap-to-enlarge affordance via `onClick`.
 *
 * @reusable
 * Usage: Mounted by CameraField, PhotoField, and ViolationCaptureBlock for each attachment ref.
 * Rules: presentation-only; no side effects beyond thumbnail resolution.
 */
import { useState, useEffect, useRef, type MouseEvent } from 'react';
import { useThumbnailResolver } from '../contracts/rendererContext';

export function AttachmentThumb({
  attachmentId,
  alt,
  onClick,
  sizeClassName = 'w-12 h-12',
}: {
  attachmentId: string;
  alt?: string;
  /** When provided, the thumbnail becomes tappable (e.g. to open a full-screen lightbox). */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** Tailwind size classes for the thumbnail box. Defaults to `w-12 h-12`. */
  sizeClassName?: string;
}) {
  const resolver = useThumbnailResolver();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!resolver);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!resolver || !attachmentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    resolver(attachmentId).then((resolved) => {
      if (mountedRef.current) {
        setUrl(resolved);
        setLoading(false);
      }
    }).catch(() => {
      if (mountedRef.current) {
        setUrl(null);
        setLoading(false);
      }
    });
  }, [resolver, attachmentId]);

  const interactive = !!onClick;

  if (!resolver) return null;
  if (loading) {
    return (
      <div className={`${sizeClassName} rounded-lg bg-gray-100 animate-pulse shrink-0`} />
    );
  }
  if (!url) {
    return (
      <div className={`${sizeClassName} rounded-lg bg-gray-100 flex items-center justify-center shrink-0`}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-300">
          <path fillRule="evenodd" d="M1 5.25A2.25 2.25 0 0 1 3.25 3h13.5A2.25 2.25 0 0 1 19 5.25v9.5A2.25 2.25 0 0 1 16.75 17H3.25A2.25 2.25 0 0 1 1 14.75v-9.5Zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 0 0 .75-.75v-2.69l-2.22-2.219a.75.75 0 0 0-1.06 0l-1.91 1.909-4.22-4.22a.75.75 0 0 0-1.06 0L2.5 11.06ZM12 7a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      aria-label={interactive ? `View ${alt ?? 'photo'} full screen` : undefined}
      className={`${sizeClassName} rounded-lg overflow-hidden shrink-0 border border-gray-200 shadow-xs p-0 ${
        interactive ? 'cursor-pointer hover:opacity-90 hover:shadow-md active:scale-95 transition-all' : 'cursor-default'
      }`}
    >
      <img
        src={url}
        alt={alt ?? 'Photo'}
        className="w-full h-full object-cover"
      />
    </button>
  );
}
